import React, { useState, useEffect } from 'react';
import { Drawer, Button, Tag, Divider, Card } from 'antd';
import {
  EditOutlined,
  DeleteOutlined,
  CloseOutlined,
  WalletOutlined,
} from '@ant-design/icons';
import dayjs from 'dayjs';
import { 
  ExpenseTransaction, 
  ExpenseCategory, 
  formatCurrency,
  // Thêm import cho wallet linking
  WalletLink,
  getExpenseWalletLinks,
  deleteExpenseWalletLink,
} from '../../services/expenseService';
import styles from './TransactionDetailModalMobile.module.css';

interface TransactionDetailModalMobileProps {
  isOpen: boolean;
  onClose: () => void;
  transaction: ExpenseTransaction | undefined;
  category: ExpenseCategory | undefined;
  onEdit: () => void;
  onDelete: () => void;
}

export const TransactionDetailModalMobile: React.FC<TransactionDetailModalMobileProps> = ({
  isOpen,
  onClose,
  transaction,
  category,
  onEdit,
  onDelete,
}) => {
  const [walletLinks, setWalletLinks] = useState<WalletLink[]>([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (isOpen && transaction) {
      loadWalletLinks();
    }
  }, [isOpen, transaction]);

  const loadWalletLinks = async () => {
    if (!transaction) return;
    try {
      const links = await getExpenseWalletLinks(transaction.id);
      setWalletLinks(links);
    } catch (error) {
      console.error('Error loading wallet links:', error);
    }
  };

  const handleUnlinkWallet = async (linkId: number) => {
    if (!transaction) return;

    try {
      setLoading(true);
      await deleteExpenseWalletLink(transaction.id, linkId);
      // Reload wallet links
      await loadWalletLinks();
    } catch (error) {
      console.error('Error unlinking wallet:', error);
    } finally {
      setLoading(false);
    }
  };

  if (!transaction) return null;

  const getCategoryIcon = (category: ExpenseCategory | undefined) => {
    if (!category) return '💰';
    return category.icon || '💰';
  };

  const getCategoryTagClass = (categoryName: string) => {
    if (categoryName?.toLowerCase().includes('ăn') || categoryName?.toLowerCase().includes('food')) {
      return styles.foodTag;
    } else if (categoryName?.toLowerCase().includes('di chuyển') || categoryName?.toLowerCase().includes('transport')) {
      return styles.transportTag;
    }
    return styles.otherTag;
  };

  const getIconContainerClass = (categoryName: string) => {
    if (categoryName?.toLowerCase().includes('ăn') || categoryName?.toLowerCase().includes('food')) {
      return styles.iconContainerFood;
    } else if (categoryName?.toLowerCase().includes('di chuyển') || categoryName?.toLowerCase().includes('transport')) {
      return styles.iconContainerTransport;
    }
    return styles.iconContainerOther;
  };

  const categoryName = category?.name || 'Không xác định';

  return (
    <Drawer
      open={isOpen}
      onClose={onClose}
      placement="bottom"
      height="80%"
      className={styles.drawer}
      title="Chi tiết giao dịch"
      closeIcon={<CloseOutlined />}
      bodyStyle={{
        padding: 0,
        userSelect: 'none',
        WebkitUserSelect: 'none',
        MozUserSelect: 'none',
        msUserSelect: 'none',
        WebkitTouchCallout: 'none',
        WebkitTapHighlightColor: 'transparent'
      }}
    >
      <div className={styles.drawerContent}>
        {/* Transaction Info */}
        <div className={styles.transactionInfo}>
          <div className={styles.transactionCard}>
            <div className={`${styles.iconContainer} ${getIconContainerClass(categoryName)}`}>
              {getCategoryIcon(category)}
            </div>
            
            <div className={styles.transactionDetails}>
              <h4 className={styles.transactionTitle}>
                {transaction.notes || `Chi tiêu cho ${categoryName}`}
              </h4>
              <Tag className={`${styles.categoryTag} ${getCategoryTagClass(categoryName)}`}>
                {getCategoryIcon(category)} {categoryName}
              </Tag>
            </div>
            
            <div className={styles.amount}>
              -{formatCurrency(transaction.amount)}
            </div>
          </div>
        </div>

        <Divider />

        {/* Transaction Details */}
        <div className={styles.detailsSection}>
          <div className={styles.detailItem}>
            <span className={styles.detailLabel}>Ngày giao dịch:</span>
            <span className={styles.detailValue}>
              {dayjs(transaction.expense_date).format('DD/MM/YYYY')}
            </span>
          </div>
          
          <div className={styles.detailItem}>
            <span className={styles.detailLabel}>Danh mục:</span>
            <span className={styles.detailValue}>{categoryName}</span>
          </div>
          
          {transaction.notes && (
            <div className={styles.detailItem}>
              <span className={styles.detailLabel}>Ghi chú:</span>
              <span className={styles.detailValue}>{transaction.notes}</span>
            </div>
          )}
          
          <div className={styles.detailItem}>
            <span className={styles.detailLabel}>Số tiền:</span>
            <span className={styles.detailValue}>
              {formatCurrency(transaction.amount)}
            </span>
          </div>
        </div>

        {/* Wallet Links Section */}
        {walletLinks.length > 0 && (
          <>
            <Divider orientation="left">
              <WalletOutlined /> Ví đã liên kết
            </Divider>
            
            <div className={styles.walletLinksSection}>
              {walletLinks.map((link) => (
                <div key={link.id} className={styles.walletLinkCard}>
                  <div className={styles.walletLinkContent}>
                    <div className={styles.walletLinkInfo}>
                      <div className={styles.walletLinkName}>
                        {link.wallet_name} ({link.wallet_source_name})
                      </div>
                      <div className={styles.walletLinkAmount}>
                        Số tiền: {formatCurrency(link.amount)}
                      </div>
                    </div>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                      <span className={styles.walletLinkTag}>Đã liên kết</span>
                      <Button
                        className={styles.unlinkButton}
                        icon={<DeleteOutlined />}
                        onClick={() => handleUnlinkWallet(link.id)}
                        loading={loading}
                      >
                        Hủy
                      </Button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </>
        )}

        {/* Action Buttons */}
        <div className={styles.actionButtons}>
          <div className={styles.actionButtonsRow}>
            <Button
              type="primary"
              icon={<EditOutlined />}
              className={styles.editButton}
              onClick={onEdit}
            >
              Sửa
            </Button>
            
            <Button
              danger
              icon={<DeleteOutlined />}
              className={styles.deleteButton}
              onClick={onDelete}
            >
              Xóa
            </Button>
          </div>
        </div>
      </div>
    </Drawer>
  );
};
